import LWElement from './../../lib/lw-element.js';
import ast from './ast.js';
import { TableColumns } from './../../lib/table-columns.js';

// Table card chrome, extracted from the identical card blocks every
// dashboard table carried: header (heading + search + slotted action
// buttons), empty state, table-wrap gating and pager. The table itself —
// thead sort wiring and the lw-for rows — is projected through the default
// slot and stays the host's: host context, host expressions, host updates.
//
//   <web-table-card lw-if="page === 'products'" class="card" key="products"
//              heading="Products" empty-icon="icon-package"
//              empty-title="No products yet" empty-description="Add one.">
//     <button slot="actions" class="btn" lw-on:click="openProductDialog(null)">Add Product</button>
//     <div slot="empty-actions" class="empty-actions">…</div>
//     <table>…</table>
//   </web-table-card>
//
// Slots: default = the <table> (projected whole — <tr> can't sit directly
// under a component tag, and a slot can't sit inside <tbody>);
// pre-actions / actions = header buttons around the search box;
// empty-description (fallback: the empty-description attribute through the
// host's search-aware helper); empty-actions; footer = between table and
// pager. Dynamic texts bind through the attributes (lw-bind:empty-title).
//
// Like web-pager, it owns no table state: the `key` attribute names the
// host's table and everything delegates to the host component's shared
// machinery (dashboard-tables.js), including the `key + 'Loaded'` flag
// convention that gates the empty state.
//
// What it does own is the COLUMN WIDTHS of the projected table — the factory
// grid's drag, double-click-to-fit and reset (lib/table-columns.js),
// remembered under colw:<key>. The table is the host's and is re-rendered by
// the host (rows on every page, sort and search; the whole table-wrap parked
// while the list is empty), so the sizer finds it afresh each time and
// re-syncs after ANY change under this card, delivered as a microtask once the
// host's update has finished — the one ordering leanweb's parent→child update
// cannot give, since a child renders BEFORE its projected content is
// refreshed. The reset button in the header shows only while there is
// something to reset (lw-if, so nothing is parked on screen), and beside it
// the factory toolbar's reload button asks the host for a fresh page of a
// server table — client-mode tables hold their whole list already and get no
// button, since one that answers nothing reads as broken.
customElements.define('web-table-card',
  class extends LWElement {
    constructor() {
      super(ast);
      this.columns = new TableColumns({
        table: () => this.querySelector('table'),
        storageKey: 'colw:' + this.key(),
        onChange: () => this.update(),
      });
      this._columnSyncPending = false;
      new MutationObserver(() => this.scheduleColumnSync())
        .observe(this, { childList: true, subtree: true, characterData: true });
      this.scheduleColumnSync();
    }

    scheduleColumnSync() {
      if (this._columnSyncPending) return;
      this._columnSyncPending = true;
      queueMicrotask(() => {
        this._columnSyncPending = false;
        this.columns.sync();
      });
    }

    hasWidths() {
      return this.columns.hasWidths();
    }

    resetWidths() {
      this.columns.reset();
    }

    canReload() {
      return this.host()?.canReload?.(this.key()) ?? false;
    }

    reload() {
      const h = this.host();
      if (!h?.reloadTable(this.key())) return;
      // A fast reload is over before anything visibly changes and the button
      // would look dead, so the icon spins one full turn regardless (the
      // factory's grid-refresh does the same).
      const button = this.querySelector('.card-actions .reload');
      button?.classList.add('spin');
      setTimeout(() => button?.classList.remove('spin'), 700);
    }

    // Nearest enclosing leanweb component (the dashboard today, a page
    // component tomorrow) — resolved per call, like web-pager does.
    host() {
      let el = this.parentElement;
      while (el && !el.localName.startsWith(leanweb.componentPrefix)) el = el.parentElement;
      return el;
    }

    key() {
      return this.getAttribute('key');
    }

    heading() {
      return this.getAttribute('heading') || '';
    }

    searchLabel() {
      return 'Search ' + (this.heading().toLowerCase() || 'table');
    }

    loaded() {
      const h = this.host();
      return h ? !!h[this.key() + 'Loaded'] : false;
    }

    total() {
      return this.host()?.tableTotal(this.key()) ?? 0;
    }

    showEmpty() {
      return this.loaded() && this.total() === 0;
    }

    hasRows() {
      return this.total() > 0;
    }

    emptyIcon() {
      return this.getAttribute('empty-icon') || 'icon-search';
    }

    emptyTitle() {
      return this.host()?.emptyTitle(this.key(), this.getAttribute('empty-title') || 'Nothing here yet') ?? '';
    }

    emptyDescription() {
      return this.host()?.emptyDescription(this.key(), this.getAttribute('empty-description') || '') ?? '';
    }

    search(value) {
      this.host()?.onSearch(this.key(), value);
    }

    showPager() {
      return this.host()?.showPager(this.key()) ?? false;
    }

    // The web-pager in our template resolves US as its host; forward its
    // delegate calls to the real table machinery. Mutating calls also update
    // the host so the projected rows re-render (mirroring what the pager
    // does for its own host).
    tableState(name) {
      return this.host()?.tableState(name) ?? { page: 1, size: 50 };
    }

    tableTotal(name) {
      return this.host()?.tableTotal(name) ?? 0;
    }

    pageInfo(name) {
      return this.host()?.pageInfo(name) ?? '';
    }

    pageCount(name) {
      return this.host()?.pageCount(name) ?? 1;
    }

    onFirstPage(name) {
      return this.host()?.onFirstPage(name) ?? true;
    }

    onLastPage(name) {
      return this.host()?.onLastPage(name) ?? true;
    }

    // One forwarder per mutating call, each followed by the host's update.
    _forward(method, ...args) {
      const h = this.host();
      if (!h) return;
      h[method](...args);
      h.update();
    }

    firstPage(name) {
      this._forward('firstPage', name);
    }

    prevPage(name) {
      this._forward('prevPage', name);
    }

    nextPage(name) {
      this._forward('nextPage', name);
    }

    lastPage(name) {
      this._forward('lastPage', name);
    }

    jumpToPage(name, value) {
      this._forward('jumpToPage', name, value);
    }

    setPageSize(name, size) {
      this._forward('setPageSize', name, size);
    }
  }
);
